import { useMemo } from 'react';
import { ScholarshipCardSkeleton } from '@components/ScholarshipCard/ScholarshipCardSkeleton';
import { CoreBox } from '@youscience/core';
import { isUndefined } from 'lodash-es';

import useApplyingScholarshipsStore from '@store/ApplyingScholarshipsStore';
import useEducationPlannerStore from '@store/EducationPlannerStore';

import { getSortedScholarships } from '@utils/scholarships';

import { FILTER_TAGS_DATA, FILTER_TAGS_COLORS, ALL_TYPES_FILTER_VALUE } from '@constants/applyingScholarships';
import { PAGE_TABS_DATA, SortByOptions, SCHOLARSHIP_CARD_SKELETONS } from '@constants/scholarships';
import { ROUTES } from '@constants/ROUTES';

import ScholarshipCard from '@components/ScholarshipCard';
import { EmptyStateText } from '@components/EmptyStateText';

import MediumTargetIcon from '@images/medium-target.svg?react';

import { ScholarshipStatus } from '@common-types';
import SortBy, { SortByValues } from '../SortBy';

import {
  StyledScholarshipCardsWrap,
  StyledPageFiltersWrap,
  StyledFilterChipsWrap,
  StyledFilterItem,
} from './MyApplications.styles';

export const MyApplications = () => {
  const { applyingScholarships } = useEducationPlannerStore((state) => state.userPlanning);
  const { sortByValues, tagValue, tagColor } = useApplyingScholarshipsStore((state) => state.pageFilters);

  const setFilter = useApplyingScholarshipsStore((state) => state.setFilter);

  const handleFilterByTag = (tag: string) => () => {
    if (tagValue === tag) return;

    setFilter({ tagValue: tag, tagColor: FILTER_TAGS_COLORS[tag] });
  };

  const handleSortBy = (sortByValues: SortByValues) => {
    setFilter({ sortByValues });
  };

  const sortedScholarships = useMemo(
    () =>
      getSortedScholarships(
        applyingScholarships?.filter((scholarship) => scholarship.applicationStatus !== ScholarshipStatus.CHANGED),
        sortByValues,
      ),
    [applyingScholarships, sortByValues],
  );

  const filteredScholarshipsByTag = useMemo(() => {
    if (tagValue !== ALL_TYPES_FILTER_VALUE) {
      return sortedScholarships?.filter((item) => item.applicationStatus === tagValue);
    }

    return sortedScholarships;
  }, [tagValue, sortedScholarships]);

  return (
    <CoreBox>
      <StyledPageFiltersWrap>
        <SortBy sortByValues={sortByValues} sortByOptions={SortByOptions} setSortByFields={handleSortBy} />

        <StyledFilterChipsWrap color={tagColor}>
          {FILTER_TAGS_DATA.map((item) => {
            const isSelected = item.value === tagValue;

            return (
              <StyledFilterItem
                data-testid="filterByTag"
                key={item.color}
                value={item.value}
                selected={isSelected}
                color={item.color}
                onClick={handleFilterByTag(item.value)}
              >
                {item.value}
              </StyledFilterItem>
            );
          })}
        </StyledFilterChipsWrap>

        <CoreBox />
      </StyledPageFiltersWrap>

      <StyledScholarshipCardsWrap>
        {isUndefined(applyingScholarships) || isUndefined(applyingScholarships)
          ? SCHOLARSHIP_CARD_SKELETONS.map((skeletonCard) => {
              return <ScholarshipCardSkeleton key={skeletonCard.id} isWiderCardType />;
            })
          : filteredScholarshipsByTag?.map((scholarship) => (
              <ScholarshipCard key={scholarship.id} scholarship={scholarship} isWiderCardType />
            ))}

        {applyingScholarships?.length === 0 ? (
          <EmptyStateText
            icon={<MediumTargetIcon />}
            title="No scholarships for application yet"
            subtitle="Hmmm. Looks like you have no scholarships for application."
            buttonTitle="Start exploring"
            buttonLink={`${ROUTES.SCHOLARSHIPS}#${PAGE_TABS_DATA.allScholarships.hash}`}
          />
        ) : null}

        {applyingScholarships?.length !== 0 && filteredScholarshipsByTag?.length === 0 ? (
          <EmptyStateText icon={<MediumTargetIcon />} title="No results" subtitle="There is no matching results." />
        ) : null}
      </StyledScholarshipCardsWrap>
    </CoreBox>
  );
};
